/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo, ReactElement, useContext } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Paper, Typography, Icon, IconButton } from '@mui/material';
import { TableContainer, Table, TableBody, TableCell, TableRow } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { TableTooltip } from '../../StyledComponents/TableTooltip';
import clsx from 'clsx';
import { RouteParam, RouteId } from '../../../router/routes';
import { Link } from 'react-router5';
import { DATA_UNAVAILABLE, MIN_VALUE_LENGTH_FOR_TOOLTIP } from '../../../utils/utils';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../assets/style/them';
import Copy from '../../CopyHOC/CopyHOC';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isNavigationAllowed } from '../../../accessibility/utils';
import { AppAbilityContext } from '../../../accessibility/ability';

export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        title: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            margin: theme.spacing(0, 'auto'),
            color: theme.palette.text.secondary,
            flexGrow: 1,
            width: '100%',
        },
        titleIcon: {
            margin: theme.spacing(0, 1),
        },
        titleLabel: {
            margin: theme.spacing(1, 1, 1, 0),
            fontSize: 18,
            fontWeight: 700,
        },
        total: {
            fontSize: 16,
            fontWeight: 500,
        },
        statusBar: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        subTitle: {
            margin: theme.spacing(1, 1, 1, 0),
            fontSize: 16,
            fontWeight: 700,
            flexGrow: 1,
        },
        status: {
            margin: theme.spacing(1, 1, 1, 0),
            fontSize: 14,
            fontWeight: 400,
        },
        tableContainer: {
            margin: theme.spacing(1, 0, 2, 0),
        },
        nameCell: {
            padding: theme.spacing(0, 2, 0, 0),
            verticalAlign: 'top',
            borderBottom: 'none',
        },
        nameIndent: {
            paddingLeft: theme.spacing(3),
        },
        name: {
            fontSize: 14,
            fontWeight: 400,
        },
        editableItem: {
            minHeight: 40,
            verticalAlign: 'middle',
        },
        valueCell: {
            padding: 0,
            borderBottom: 'none',
        },
        value: {
            fontSize: 14,
            fontWeight: 700,
            display: '-webkit-box',
            '-webkit-line-clamp': 2,
            '-webkit-box-orient': 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-all',
        },
        unavailableValue: {
            color: theme.palette.error.main,
        },
        linkText: {
            color: theme.palette.link.main,
            textDecoration: 'underline',
        },
        editIcon: {
            marginLeft: theme.spacing(1),
        },
        copyIcon: {
            padding: 0,
            marginRight: theme.spacing(1),
        },
    }),
);

interface NameValueLinkData {
    routeId: RouteId;
    routeParamName?: RouteParam;
    routeParamValue?: string;
    valueToShow?: string;
    onClick?(): void;
}

export interface NameValueDataItem {
    name: string;
    value: string | string[];

    nameColor?: string;
    isNameIndent?: boolean;
    nameDescription?: string; //tooltip on name, some explanation text

    valueColor?: string;
    valueTooltip?: boolean; //tooltip on value, that will be equal to the value (for long value strings)
    valueDescription?: string; //tooltip on value, some explanation text

    isCopyValue?: boolean;
    link?: NameValueLinkData;
    links?: NameValueLinkData[];

    testDataName?: string;
    testDataValue?: string | string[] | number;
    testId?: string;

    onEditValue?(): void;
    customValueContent?(): ReactElement;
}

interface NameValueGroupProps {
    data?: NameValueDataItem[];
    title?: string;
    total?: number;
    icon?: string;
    subTitle?: string;
    status?: string;
    statusColor?: string;
    statusTooltip?: string;
    valueCellWidthInPercent?: number;
    valueAlign: 'right' | 'left' | 'center';
    backgroundColor?: string;
    elevation?: number;
    padding?: number;
    testId?: string;
}

const NameValueGroup = (props: NameValueGroupProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const ability = useContext(AppAbilityContext);

    const {
        title,
        total,
        subTitle,
        status,
        statusColor,
        statusTooltip,
        icon,
        valueAlign,
        data,
        valueCellWidthInPercent,
        backgroundColor,
        elevation,
        padding,
        testId,
    } = props;

    let nameWidth = '40%';
    let valueWidth = '60%';
    if (valueCellWidthInPercent) {
        valueWidth = `${valueCellWidthInPercent}%`;
        nameWidth = `${100 - valueCellWidthInPercent}%`;
    }

    return (
        <Paper
            data-testid={testId}
            className={classes.root}
            elevation={elevation ? elevation : 0}
            style={{
                backgroundColor: backgroundColor ? backgroundColor : null,
                padding: padding !== undefined ? theme.spacing(padding) : theme.spacing(1),
            }}
        >
            {title ? (
                <div data-testid="registry-name-value-title" className={classes.title}>
                    <Icon className={classes.titleIcon}>{icon}</Icon>
                    <Typography className={classes.titleLabel} noWrap variant={'subtitle2'}>
                        {title}
                    </Typography>
                    {total ? (
                        <Typography className={classes.total} variant={'subtitle2'} noWrap>
                            {`(${t('app.total')}: ${total})`}
                        </Typography>
                    ) : null}
                </div>
            ) : null}
            {subTitle || status ? (
                <div className={classes.statusBar}>
                    <Typography className={classes.subTitle} noWrap variant={'subtitle2'}>
                        {subTitle}
                    </Typography>
                    <TableTooltip title={statusTooltip ? statusTooltip : ''} placement="top-start">
                        <Typography
                            data-testid="registry-name-value-status"
                            className={classes.status}
                            noWrap
                            variant={'subtitle2'}
                            style={statusColor ? { color: statusColor } : null}
                        >
                            {status}
                        </Typography>
                    </TableTooltip>
                </div>
            ) : null}
            {data && data.length !== 0 ? (
                <TableContainer id="nameValueContainer" className={classes.tableContainer}>
                    <Table>
                        <TableBody>
                            {data.map((item, itemIndex) => {
                                return (
                                    <TableRow data-testid={item.testId} key={itemIndex}>
                                        <TableCell
                                            data-key={item.testDataName ? item.testDataName : null}
                                            className={clsx(
                                                classes.nameCell,
                                                item.isNameIndent ? classes.nameIndent : null,
                                                item.onEditValue ? classes.editableItem : null,
                                            )}
                                            align="left"
                                            style={{ width: nameWidth }}
                                        >
                                            <TableTooltip
                                                title={item.nameDescription ? item.nameDescription : ''}
                                                placement="top-start"
                                            >
                                                <Typography
                                                    className={classes.name}
                                                    style={{ color: `${item.nameColor}` }}
                                                >
                                                    {item.name}
                                                </Typography>
                                            </TableTooltip>
                                        </TableCell>
                                        <TableTooltip
                                            title={
                                                item.valueTooltip && item.value.length > MIN_VALUE_LENGTH_FOR_TOOLTIP
                                                    ? item.value
                                                    : item.valueDescription
                                                      ? item.valueDescription
                                                      : ''
                                            }
                                            placement="top-start"
                                        >
                                            <TableCell
                                                data-value={item.testDataValue ? item.testDataValue : null}
                                                className={classes.valueCell}
                                                align={valueAlign}
                                                style={{ width: valueWidth }}
                                            >
                                                {item.links ? (
                                                    item.links.map((link, index) => {
                                                        return isNavigationAllowed(ability, link.routeId) ? (
                                                            <Link
                                                                key={index}
                                                                routeName={link.routeId}
                                                                routeParams={
                                                                    link.routeParamName && link.routeParamValue
                                                                        ? {
                                                                              [link.routeParamName]:
                                                                                  link.routeParamValue,
                                                                          }
                                                                        : null
                                                                }
                                                                onClick={link.onClick}
                                                            >
                                                                <Typography
                                                                    className={clsx(classes.value, classes.linkText)}
                                                                    style={{ color: `${item.valueColor}` }}
                                                                >
                                                                    {link.valueToShow
                                                                        ? link.valueToShow
                                                                        : link.routeParamValue}
                                                                </Typography>
                                                            </Link>
                                                        ) : (
                                                            <Typography key={index} className={classes.value}>
                                                                {link.valueToShow
                                                                    ? link.valueToShow
                                                                    : link.routeParamValue}
                                                            </Typography>
                                                        );
                                                    })
                                                ) : item.link && isNavigationAllowed(ability, item.link.routeId) ? (
                                                    <Link
                                                        routeName={item.link.routeId}
                                                        routeParams={
                                                            item.link.routeParamName && item.link.routeParamValue
                                                                ? {
                                                                      [item.link.routeParamName]:
                                                                          item.link.routeParamValue,
                                                                  }
                                                                : null
                                                        }
                                                        onClick={item.link.onClick}
                                                    >
                                                        <Typography
                                                            className={clsx(classes.value, classes.linkText)}
                                                            style={{ color: `${item.valueColor}` }}
                                                        >
                                                            {item.link.valueToShow ? item.link.valueToShow : item.value}
                                                        </Typography>
                                                    </Link>
                                                ) : item.customValueContent ? (
                                                    item.customValueContent()
                                                ) : Array.isArray(item.value) ? (
                                                    item.value.map((text, index) => {
                                                        return (
                                                            <Typography
                                                                key={index}
                                                                className={classes.value}
                                                                style={{ color: `${item.valueColor}` }}
                                                            >
                                                                {text}
                                                            </Typography>
                                                        );
                                                    })
                                                ) : (
                                                    <Typography
                                                        className={clsx(
                                                            classes.value,
                                                            item.value === DATA_UNAVAILABLE
                                                                ? classes.unavailableValue
                                                                : null,
                                                        )}
                                                        style={{ color: `${item.valueColor}` }}
                                                    >
                                                        {item.isCopyValue && (
                                                            <Copy onCopy={null} text={item.value}>
                                                                <TableTooltip title={t('app.copy')} placement="top">
                                                                    <IconButton
                                                                        color={'primary'}
                                                                        className={classes.copyIcon}
                                                                    >
                                                                        <ContentCopyIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableTooltip>
                                                            </Copy>
                                                        )}
                                                        {item.value}
                                                        {item.onEditValue && (
                                                            <TableTooltip title={t('app.edit')} placement="top">
                                                                <IconButton
                                                                    className={classes.editIcon}
                                                                    onClick={item.onEditValue}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </TableTooltip>
                                                        )}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableTooltip>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}
        </Paper>
    );
};

export default memo(NameValueGroup);
